import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import _ from 'lodash';
import { Link } from 'gatsby';
import type { ISbStoryData } from '@storyblok/js';
import type {
  ProductComparisonProduct as IProductComparisonProduct,
  ProductComparisonProductVariant,
} from '../../../../.storyblok/types/255103/storyblok-components';
import { buttonLengthConstants, buttonVariantConstants } from '../../../common/constants';
import { useRootState } from '../../../context/root-state.context';
import { getStoryblokAspectRatio } from '../../../utils/storyblokAsset';
import { renderRichTextReact } from '../../../utils/storyblokRichText';
import PriceRange from '../../Product/PriceRange';
import LooxRating from '../../Product/LooxRating';
import ButtonRegular from '../../Button/ButtonRegular';
import ProductComparisonRowFixedScroll from '../../Product/ProductComparisonRowFixedScroll';
import HorizontalCarouselPillNav from '../../Carousels/HorizontalCarouselPillNav';
import ShopPayInstallmentsLogo from '../../../img/shop-pay-affirm-logo-black.png';

interface ProductComparisonProductProps {
  blok: IProductComparisonProduct;
  column?: string;
  fixedScrollThreshold?: boolean;
}

const ProductComparisonProduct = ({ blok, column, fixedScrollThreshold }: ProductComparisonProductProps) => {
  const { productVariantStories } = useRootState();

  const productVariantStoriesFiltered = useMemo(() => {
    return productVariantStories.filter((story) => blok.productVariants.includes(story.uuid));
  }, [blok, productVariantStories]);

  const productVariants: ProductComparisonProductVariant[] = useMemo(() => {
    return productVariantStoriesFiltered
      .map((productVariantStory: ISbStoryData<ProductComparisonProductVariant>) => productVariantStory.content)
      .sort(
        (a: ProductComparisonProductVariant, b: ProductComparisonProductVariant) => Number(a.order) - Number(b.order),
      );
  }, [productVariantStoriesFiltered]);

  const [activeVariant, setActiveVariant] = useState<ProductComparisonProductVariant | undefined>(() =>
    productVariants.find((el) => el),
  );

  // Re-sync if productVariants changes reference after mount (e.g. Storyblok editor updates) —
  // the lazy initializer above already covers the correct value on first render/SSG.
  useEffect(() => {
    if (!_.isEmpty(productVariants)) {
      setActiveVariant(productVariants.find((el) => el));
    }
  }, [productVariants]);

  const handleUpdateActiveVariant = (index: number) => {
    setActiveVariant(productVariants[index]);
  };

  return (
    <div className={'w-full flex flex-col flex-nowrap gap-y-4'}>
      {/* fixed scroll container */}
      <div
        className={clsx('bg-beige-100 top-0 w-[50vw] z-[20]', {
          fixed: fixedScrollThreshold,
          hidden: !fixedScrollThreshold,
          'left-0 pl-[6.25%] md:pl-[10%] lg:pl-[8.3333%] pr-[8px]': column === 'left',
          'right-0 pr-[6.25%] md:pr-[10%] lg:pr-[8.3333%] pl-[8px]': column === 'right',
        })}
      >
        {!_.isEmpty(activeVariant) && <ProductComparisonRowFixedScroll activeVariant={activeVariant} />}
      </div>

      {/* logotype */}
      {!_.isEmpty(activeVariant) && (
        <Link to={activeVariant.slug ? `/shop/${activeVariant.slug}` : ''}>
          {/* The CMS width/height fields are optional and in practice usually empty, which
              left this image unsized — Lighthouse attributes ~0.209 of /shop's CLS to it
              alone. getStoryblokAspectRatio falls back to the dimensions Storyblok encodes
              in the asset URL, so the box is reserved whether or not an editor filled the
              fields in. */}
          <img
            alt={activeVariant.logo?.alt ?? ''}
            className="w-full max-w-[85px] sm:max-w-[120px] md:max-w-[150px]"
            style={{ aspectRatio: getStoryblokAspectRatio(activeVariant.logo) }}
            src={activeVariant.logo?.filename ?? ''}
          />
        </Link>
      )}

      {/* image gallery */}
      {!_.isEmpty(activeVariant) && (
        <HorizontalCarouselPillNav onSlideChange={handleUpdateActiveVariant} slides={productVariants} />
      )}

      {/* price range, loox, shop button, Shop Pay Installments */}
      <div className="flex flex-row flex-wrap gap-y-4">
        <div className="flex flex-col md:flex-row basis-full">
          <div className="flex flex-col basis-1/2 items-center md:items-start">
            {/* price */}
            {!_.isEmpty(activeVariant) && (
              <PriceRange
                className="typography-body-m lg:typography-body-xxl !font-bold !text-navy-600"
                minPrice={Number(activeVariant.price)}
              />
            )}

            {/* loox */}
            {!_.isEmpty(activeVariant) && (
              <LooxRating link={`/shop/${activeVariant.slug}#productReviews`} shopifyId={activeVariant.shopifyId} />
            )}
          </div>

          <div className="flex flex-col basis-1/2 text-center md:text-right">
            {!_.isEmpty(activeVariant) && (
              <Link to={activeVariant.slug ? `/shop/${activeVariant.slug}` : ''} className="w-full">
                <ButtonRegular
                  className="!max-w-[96px] md:!max-w-[138px]"
                  length={buttonLengthConstants.NORMAL}
                  variant={buttonVariantConstants.PRIMARY_ORANGE}
                >
                  SHOP
                </ButtonRegular>
              </Link>
            )}
          </div>
        </div>

        <div className="basis-full">
          {/* Shop Pay Installments */}
          {!_.isEmpty(activeVariant) && (
            <div className="flex flex-col md:flex-row items-center md:items-end">
              <img
                className="w-[166px] min-w-[166px] aspect-[2097/653]"
                alt="Shop Pay Installments"
                src={ShopPayInstallmentsLogo}
              />
              <span className="typography-body-xxs sm:typography-body-s !text-navy-600 text-center md:text-left">
                {activeVariant.shopPayInstallmentsText}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* description */}
      {!_.isEmpty(activeVariant) && (
        <div
          className="typography-body-s sm:typography-body-m !text-navy-600 !font-utopiaStdCaption text-center
            md:text-left"
        >
          {renderRichTextReact(activeVariant.description)}
        </div>
      )}
    </div>
  );
};

export default React.memo(ProductComparisonProduct);
